import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import io from "socket.io-client";
import { cookie } from "../cookies/cookies";
import { useSelector } from "react-redux";

export const SocketContext = createContext();

const onlineKey = (id) => (id == null || id === "" ? null : String(id));

const addOnlineId = (prev, userId) => {
  const key = onlineKey(userId);
  if (!key) return prev;
  return prev.some((id) => String(id) === key) ? prev : [...prev, key];
};

const removeOnlineId = (prev, userId) => {
  const key = onlineKey(userId);
  if (!key) return prev;
  return prev.filter((id) => String(id) !== key);
};

const getMessageKey = (message) => {
  if (message?.id) return `id:${message.id}`;
  return [
    message?.threadId || "",
    message?.senderId || "",
    message?.contentType || "",
    message?.content || "",
    message?.createdAt || "",
  ].join("|");
};

const mergeMessages = (existingMessages = [], incomingMessages = []) => {
  const messageMap = new Map();

  [...existingMessages, ...incomingMessages].forEach((message) => {
    const key = getMessageKey(message);
    if (!key) return;

    const previous = messageMap.get(key) || {};
    messageMap.set(key, { ...previous, ...message });
  });

  return Array.from(messageMap.values()).sort((a, b) => {
    const first = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
    const second = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
    return first - second;
  });
};

export const SocketContextProvider = ({ children }) => {
  const [socket, setSocket] = useState();
  const [threadId, setThreadId] = useState(null);
  const [threadMessages, setThreadMessages] = useState({});
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);

  const token = cookie.get("token");
  const scrollRef = useRef(null);
  const profileDetails = useSelector((state) => state?.auth?.profileDetails);
  const currentThreadId = useSelector(
    (state) => state?.persionalShopData?.threadUser
  );
  const profileDetailsRef = useRef(profileDetails);
  useEffect(() => {
    profileDetailsRef.current = profileDetails;
  }, [profileDetails]);

  const connectSocket = useCallback(() => {
    const socketUrl = process.env.REACT_APP_SOCKET_URL || "http://localhost:3030";
    const newSocket = io(socketUrl, {
      auth: { token },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 2000,
    });
    setSocket(newSocket);
  }, [token]);

  // connect socket when token exists
  useEffect(() => {
    if (token && !socket) connectSocket();
  }, [token, socket, connectSocket]);

  useEffect(() => {
    if (!socket) {
      setSocketConnected(false);
      return undefined;
    }
    const sync = () => setSocketConnected(socket.connected);
    const onConnect = () => {
      setSocketConnected(true);
      // Emit user_online when socket connects
      const userId = profileDetailsRef.current?.id;
      if (userId) {
        socket.emit("user_online", { userId });
      }
    };
    const onDisconnect = () => setSocketConnected(false);
    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    sync();
    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
    };
  }, [socket]);

  // Best-effort: tell server user went offline when the tab is closed (logout path also emits).
  useEffect(() => {
    if (!socket) return;
    const onPageHide = (e) => {
      if (e.persisted) return;
      const userId = profileDetailsRef.current?.id;
      if (userId == null) return;
      try {
        socket.emit("user_offline", { userId });
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("pagehide", onPageHide);
    return () => window.removeEventListener("pagehide", onPageHide);
  }, [socket]);

  // update threadId when Redux changes
  useEffect(() => {
    setThreadId(currentThreadId?.id);
  }, [currentThreadId?.id]);

  const setMessagesForThread = useCallback((targetThreadId, updater) => {
    if (!targetThreadId) return;

    setThreadMessages((prev) => {
      const currentMessages = prev[targetThreadId] || [];
      const nextMessages =
        typeof updater === "function" ? updater(currentMessages) : updater;

      return {
        ...prev,
        [targetThreadId]: Array.isArray(nextMessages) ? nextMessages : currentMessages,
      };
    });
  }, []);

  useEffect(() => {
    if (!socket) return;

    const handleReceiveMessage = (response) => {
      const { sender, message, messageType, threadId: msgThreadId } = response;
      if (!msgThreadId) return;

      setMessagesForThread(msgThreadId, (prev) => [
        ...mergeMessages(prev, [{
          id: response?.id,
          threadId: msgThreadId,
          senderId: sender?.id,
          createdAt: response?.createdAt,
          contentType: messageType?.toLowerCase() === "image" ? "IMAGE" : "TEXT",
          content: message,
          isSender: sender?.id === profileDetailsRef.current?.id,
        }]),
      ]);
    };

    const handleDisconnect = () => {
      setThreadId(null);
    };

    socket.on("receive_message", handleReceiveMessage);

    socket.on("unread_updated", ({ threadId, updatedBy }) => {
      console.log("📬 Received unread_updated:", { threadId, updatedBy, currentActiveThread: activeThreadId });
      if (updatedBy === profileDetails?.id) {
        console.log("📬 Ignoring unread_updated - message from self");
        return;
      }
      if (activeThreadId === threadId) {
        console.log("📬 Ignoring unread_updated - currently viewing this thread");
        return;
      }

      setUnreadCounts((prev) => {
        const exists = prev.find((t) => t.threadId === threadId);

        if (!exists) {
          const newCount = [{ threadId, updatedBy, unreadCount: 1 }];
          console.log("📬 Adding new unread count:", newCount);
          return [...prev, ...newCount];
        }

        const updated = prev.map((t) =>
          t.threadId === threadId
            ? { ...t, unreadCount: t.unreadCount + 1 }
            : t
        );
        console.log("📬 Updated unread counts:", updated);
        return updated;
      });
    });

    socket.on("unread_cleared", ({ threadId }) => {
      console.log("📬 Received unread_cleared for thread:", threadId);
      setUnreadCounts((prev) => {
        const updated = prev.map((t) =>
          t.threadId === threadId ? { ...t, unreadCount: 0 } : t
        );
        console.log("📬 Cleared unread counts:", updated);
        return updated;
      });
    });

    const handleUserOnline = ({ userId }) => {
      console.log("🟢 Received user_online event for userId: ====>", userId);
      console.log("🟢 Current onlineUsers before update:", onlineUsers);
      setOnlineUsers((prev) => {
        const updated = addOnlineId(prev, userId);
        console.log("🟢 Updated onlineUsers after adding:", updated);
        return updated;
      });
    };

    const handleUserOffline = ({ userId }) => {
      console.log("🔴 Received user_offline event for userId: ====>", userId);
      console.log("🔴 Current onlineUsers before update:", onlineUsers);
      setOnlineUsers((prev) => {
        const updated = removeOnlineId(prev, userId);
        console.log("🔴 Updated onlineUsers after removing:", updated);
        return updated;
      });
    };

    socket.on("user_online", handleUserOnline);

    socket.on("logout_user", handleUserOffline);

    socket.on("user_offline", handleUserOffline);

    socket.on("online_users_by_type", (payload) => {
      const customers = payload?.customers;
      const ids = [];
      if (Array.isArray(customers)) {
        customers.forEach((c) => {
          if (c?.id != null) ids.push(c.id);
        });
      } else if (customers?.id != null) {
        ids.push(customers.id);
      }
      if (!ids.length) return;
      setOnlineUsers((prev) => {
        let next = prev;
        ids.forEach((id) => {
          next = addOnlineId(next, id);
        });
        return next;
      });
    });

    socket.on("disconnect", handleDisconnect);

    return () => {
      socket.off("receive_message", handleReceiveMessage);
      socket.off("unread_updated");
      socket.off("unread_cleared");
      socket.off("user_online", handleUserOnline);
      socket.off("logout_user", handleUserOffline);
      socket.off("user_offline", handleUserOffline);
      socket.off("online_users_by_type");
      socket.off("disconnect", handleDisconnect);
    };
  }, [socket, activeThreadId, profileDetails?.id, setMessagesForThread, onlineUsers]);

  return (
    <SocketContext.Provider
      value={{
        socket,
        threadId,
        setThreadId,
        threadMessages,
        setMessagesForThread,
        scrollRef,
        onlineUsers,
        socketConnected,
        unreadCounts,
        setActiveThreadId,
        setUnreadCounts,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export const useSocketContext = () => useContext(SocketContext);

export { isUserOnline, resolvePeerOnlineDisplay } from "../utils/presence";
